// Combat resolution — implements the formal spec from docs/gameplay.md.
// Pure: no DB calls, no side effects.

import type { AttackResult, DiceRoll, GameEvent, GameState, PlayerId, TerritoryId } from './types.js'
import { createRng, rollDice } from './dice.js'
import type { Rng } from './dice.js'
// Rng is re-exported from src/engine/index.ts via the canonical
// `./dice.js` path — no need to re-export it here too.
import type { Card } from './cards.js'
import { rollOutcomes } from './combatOdds.js'
import { canAttack } from './turn.js'
import { markHypotheticalState } from './hypotheticalState.js'
import { DEFAULT_DICE_SIDES, attackDiceSides, defendDiceSides, armyHeadroom, areAllies } from '@warcarp/api-types'
import { activeTeams, teamEliminationEvents } from './teams.js'


/**
 * Resolve a single attack roll.
 *
 * Formal spec (from docs/gameplay.md):
 *   attackerDice  = min(requestedAttackerDice ?? 3, attackerArmies - 1)
 *   defenderDice  = min(2, defenderArmies)
 *   Compare sorted-descending pairs; ties go to defender.
 *   conquered: true iff defenderArmies - defenderLost === 0
 *
 * `requestedAttackerDice` honours the player's chosen dice count (1/2/3); it is
 * clamped to the legal max (min(3, armies-1)) and floored at 1. When omitted the
 * attacker rolls the max — the default for sims/tests that don't choose a count.
 *
 * Dice COUNTS are the classic Risk rule. Dice SIZES come from the border being
 * fought across and default to six: each side rolls its own, so a d8 tower does
 * not make the territory defending against it roll d8 too. See
 * `Territory.borderDice` / `diceSidesFor()`.
 */
export function resolveAttack(
  attackerArmies: number,
  defenderArmies: number,
  rng: Rng,
  requestedAttackerDice?: number,
  attackerSides: number = DEFAULT_DICE_SIDES,
  defenderSides: number = DEFAULT_DICE_SIDES,
): AttackResult {
  const maxAttackerDice = Math.min(3, attackerArmies - 1)
  const numAttackerDice =
    requestedAttackerDice != null
      ? Math.max(1, Math.min(requestedAttackerDice, maxAttackerDice))
      : maxAttackerDice
  const numDefenderDice = Math.min(2, defenderArmies)

  // Each side's dice are sorted descending by rollDice.
  const attackerRolls = rollDice(rng, numAttackerDice, attackerSides)
  const defenderRolls = rollDice(rng, numDefenderDice, defenderSides)

  const pairs = Math.min(numAttackerDice, numDefenderDice)
  let attackerLost = 0
  let defenderLost = 0

  for (let i = 0; i < pairs; i++) {
    // Both arrays have at least `pairs` elements (bounded by min above).
    const a = attackerRolls[i] as number
    const d = defenderRolls[i] as number
    if (a > d) {
      defenderLost++
    } else {
      // Tie goes to defender — attacker loses.
      attackerLost++
    }
  }

  const conquered = defenderArmies - defenderLost === 0

  return {
    attackerLost,
    defenderLost,
    conquered,
    roll: {
      attacker: attackerRolls,
      defender: defenderRolls,
    },
  }
}

export interface AttackMove {
  fromTerritoryId: TerritoryId
  toTerritoryId: TerritoryId
  /** Number of dice to roll. The engine clamps this to min(3, source.armies - 1). */
  diceCount: number
}

export interface ApplyAttackResult {
  state: GameState
  attackerLost: number
  defenderLost: number
  conquered: boolean
  roll: DiceRoll
  /** Players newly eliminated by this attack (no territories remaining). */
  eliminated: PlayerId[]
}

/** Shared territory lookup and effective dice count for actual/search combat. */
function attackContext(state: GameState, move: AttackMove) {
  const attackerId = state.players[state.turnIndex] as PlayerId
  const fromTerritory = state.map.territories.find((t) => t.id === move.fromTerritoryId)
  const toTerritory = state.map.territories.find((t) => t.id === move.toTerritoryId)

  if (!fromTerritory) throw new Error(`Unknown source territory '${move.fromTerritoryId}'`)
  if (!toTerritory) throw new Error(`Unknown target territory '${move.toTerritoryId}'`)

  // Number of attacker dice = the player's REQUESTED count, clamped to the legal
  // max (min(3, armies-1)). validateMove already guarantees the range, but we
  // clamp defensively. This is also the number of armies that move into a
  // conquered territory. Previously this ignored move.diceCount and always used
  // the max — so picking 1 or 2 still rolled 3.
  const attackerDiceCount = Math.max(1, Math.min(3, fromTerritory.armies - 1, move.diceCount))

  return { attackerId, fromTerritory, toTerritory, attackerDiceCount }
}

/**
 * Resolve one real attack using the state's RNG, without mutating the input.
 * The caller verifies phase/cap, ownership, adjacency and source armies, and
 * decides whether to advance phase afterward. The attacker is the current player.
 */
export function applyAttack(state: GameState, move: AttackMove): ApplyAttackResult {
  const context = attackContext(state, move)
  const { fromTerritory, toTerritory, attackerDiceCount } = context

  // Roll the dice using the state's RNG seed, then capture the advanced seed.
  const rng = createRng(state.rngSeed)
  // Each side rolls its OWN border's dice: the attacker across from→to, the
  // defender across to→from. On a one-way border the defender has no reverse
  // entry, so it falls back to the standard six.
  const result = resolveAttack(
    fromTerritory.armies,
    toTerritory.armies,
    rng,
    attackerDiceCount,
    attackDiceSides(fromTerritory, toTerritory.id),
    defendDiceSides(toTerritory, fromTerritory.id),
  )
  const newSeed = rng.nextSeed()

  return applyResolvedAttack(state, move, context, result, newSeed)
}

/**
 * Exact one-roll branches for search, using the same transition as real combat.
 * The rolls in each branch are representative hypothetical rolls, not observations.
 * Branches retain the input RNG seed and must never be persisted as actual play.
 * Like applyAttack, the caller owns phase, ownership and adjacency validation.
 */
export function enumerateAttackOutcomes(state: GameState, move: AttackMove): Array<ApplyAttackResult & { probability: number }> {
  const context = attackContext(state, move)
  const { fromTerritory, toTerritory, attackerDiceCount } = context
  const defenderDiceCount = Math.min(2, toTerritory.armies)
  const attackerSides = attackDiceSides(fromTerritory, toTerritory.id)
  const defenderSides = defendDiceSides(toTerritory, fromTerritory.id)
  if (!Number.isInteger(move.diceCount) || move.diceCount < 1 || move.diceCount > 3 ||
      !Number.isSafeInteger(fromTerritory.armies) || fromTerritory.armies < 2 ||
      !Number.isSafeInteger(toTerritory.armies) || toTerritory.armies < 1 ||
      !Number.isSafeInteger(attackerSides) || attackerSides < 1 ||
      !Number.isSafeInteger(defenderSides) || defenderSides < 1) {
    throw new Error('Invalid exact attack parameters')
  }
  return rollOutcomes(attackerDiceCount, defenderDiceCount, attackerSides, defenderSides).map(
    ([probability, attackerLost, defenderLost]) => {
      // Against defender ones, each attacker two wins one pair; remaining ones
      // lose ties. A positive-probability defender loss requires attackerSides >=2.
      const result: AttackResult = {
        attackerLost, defenderLost, conquered: toTerritory.armies === defenderLost,
        roll: {
          attacker: Array.from({ length: attackerDiceCount }, (_, i) => i < defenderLost ? 2 : 1),
          defender: Array<number>(defenderDiceCount).fill(1),
        },
      }
      const branch = applyResolvedAttack(state, move, context, result, state.rngSeed)
      return { probability, ...branch, state: markHypotheticalState(branch.state) }
    },
  )
}

/** No search-specific copy of conquest, caps, card inheritance or interrupts. */
function applyResolvedAttack(
  state: GameState,
  move: AttackMove,
  context: ReturnType<typeof attackContext>,
  result: AttackResult,
  newSeed: string,
): ApplyAttackResult {
  const { attackerId, toTerritory, attackerDiceCount } = context

  // Apply combat losses + (optional) conquest transfer in one pass.
  //   - Source loses its attacker-side combat losses, plus
  //     attackerDiceCount armies if the conquest happened (those move
  //     into the captured territory).
  //   - Target loses its defender-side combat losses; on conquest its
  //     ownership flips and its armies become exactly
  //     attackerDiceCount (the moved force).
  // A conquered territory with a troop cap takes what it can hold; the surplus
  // stays in the source rather than evaporating.
  // At least 1 — a conquered territory can never end up empty, whatever the cap.
  const movedIn = result.conquered
    ? Math.max(1, Math.min(attackerDiceCount, armyHeadroom({ armies: 0, maxArmies: toTerritory.maxArmies })))
    : 0
  const newTerritories = state.map.territories.map((t) => {
    if (t.id === move.fromTerritoryId) {
      const armiesAfter = t.armies - result.attackerLost - movedIn
      return { ...t, armies: armiesAfter }
    }
    if (t.id === move.toTerritoryId) {
      if (result.conquered) {
        return { ...t, ownerId: attackerId, armies: movedIn }
      }
      return { ...t, armies: t.armies - result.defenderLost }
    }
    return t
  })

  // Recompute eliminated players from territory ownership.
  const newlyEliminated = state.players.filter(
    (pid) =>
      !state.eliminated.includes(pid) && !newTerritories.some((t) => t.ownerId === pid),
  )
  const eliminated = newlyEliminated.length === 0
    ? state.eliminated
    : [...state.eliminated, ...newlyEliminated]

  // Detect game-ending attack: after updating eliminated, is there only one
  // active player left? If so, the bonus armies are moot — the game is over.
  // We skip post-elim-placement entirely and leave the phase as 'attack' so
  // the route's normal shouldEndGame → COMPLETE path fires immediately.
  // Counts sides, not players: in a team game the attack ends the game only
  // when it leaves a single team standing (activeTeams is the active players
  // in free-for-all).
  const afterBoard: GameState = { ...state, map: { ...state.map, territories: newTerritories }, eliminated }
  const isGameEndingAttack = result.conquered && newlyEliminated.length > 0 && activeTeams(afterBoard).length <= 1

  // Track post-conquest transfer eligibility. Setting lastConquest tells
  // the route layer that the next move from this player may legally be a
  // POST /turn/transfer to move additional troops into the new territory.
  // Cleared on any non-conquering attack so a stale conquest pair from an
  // earlier roll can't be used after a follow-up attack misses.
  // Also cleared on a game-ending attack: the game is over, so the free-
  // fortify window is moot and should not appear in the final game state.
  const lastConquest = result.conquered && !isGameEndingAttack
    ? {
        fromTerritoryId: move.fromTerritoryId,
        toTerritoryId: move.toTerritoryId,
        armiesAlreadyMoved: attackerDiceCount,
      }
    : null

  // ─── Cards + eliminate-player bonus ──────────────────────────────────────
  //
  // On a conquering attack:
  //   1. Flip `conqueredThisTurn` true so the turn-end transition draws a
  //      card for the attacker. Only the FIRST conquest of the turn triggers
  //      the draw — subsequent conquests don't grant additional cards.
  //
  // When the attack also eliminated one or more players:
  //   2. Transfer ALL of each eliminated player's cards into the attacker's
  //      hand (concatenate in elimination order). Eliminated players' hands
  //      are cleared (kept as empty arrays — keys preserved).
  //   3. Add `config.eliminatePlayerBonus` to the attacker's bonusArmies for
  //      each player eliminated.
  //
  // Note: the 5-card cap is normally re-checked at placement-phase entry
  // (turn.ts) and surfaced as `mustTurnInCards`, which gates placement until the
  // player drops below 5. After an elimination the enforcement point depends on
  // the elim bonus (see below): with a bonus, the interrupt always opens and a
  // >=5 hand must turn in; with NO bonus, the interrupt opens ONLY for a 6+ hand
  // (a <=5 hand is caught next turn instead).
  //
  // Exception: when the elimination ends the game (isGameEndingAttack), the
  // bonus armies are moot — we still transfer inherited cards (for the event
  // log) but skip the bonusArmies update and suppress post-elim-placement.
  let nextCards = state.cards
  let nextBonusArmies = state.bonusArmies
  let postElimPhase = false
  let postElimMustTurnIn = false
  if (result.conquered && newlyEliminated.length > 0) {
    const inheritedCards = newlyEliminated.flatMap((pid) => state.cards?.[pid] ?? [])
    let attackerHandAfter: Card[] = []
    if (inheritedCards.length > 0 || state.cards !== undefined) {
      const cards: Record<PlayerId, Card[]> = { ...(state.cards ?? {}) }
      for (const pid of newlyEliminated) {
        cards[pid] = []
      }
      const attackerHand = cards[attackerId] ?? []
      cards[attackerId] = [...attackerHand, ...inheritedCards]
      nextCards = cards
      attackerHandAfter = cards[attackerId]!
    }

    // Only grant and trigger bonus when the game is NOT ending here.
    // When it IS ending, the bonus armies are never placed — skip both
    // the bonusArmies update and the postElimPhase flag so the route's
    // shouldEndGame check sees a single survivor and goes straight to COMPLETE.
    if (!isGameEndingAttack) {
      const elimBonus = state.config.eliminatePlayerBonus * newlyEliminated.length
      if (elimBonus > 0) {
        const bonusArmies: Record<PlayerId, number> = { ...(state.bonusArmies ?? {}) }
        bonusArmies[attackerId] = (bonusArmies[attackerId] ?? 0) + elimBonus
        nextBonusArmies = bonusArmies
        // An elimination bonus ALWAYS opens the interrupt placement phase so the
        // attacker places the bonus troops before resuming attacks. If inherited
        // cards pushed the hand to >=5, they must turn in a set first (normal cap).
        postElimPhase = true
        postElimMustTurnIn = attackerHandAfter.length >= 5
      } else if (attackerHandAfter.length >= 6) {
        // No troop bonus, so normally there is NO interrupt — the 5-card cap is
        // re-checked at the attacker's next placement (turn.ts). The exception:
        // if inheriting the eliminated player's cards pushed the hand to 6+, the
        // cap must be enforced NOW. Open the interrupt purely to force the turn-in
        // (no bonus troops; the placement budget is whatever the traded-in set
        // grants). A hand of exactly 5 here is left for the next turn.
        postElimPhase = true
        postElimMustTurnIn = true
      }
    }
  }

  const conqueredThisTurn = result.conquered ? true : state.conqueredThisTurn

  // Public aggression ledger (see GameState.aggression). Neutral defenders have
  // no ownerId and are not tracked; nobody holds a grudge against a wilderness.
  const ledgerDefender = toTerritory.ownerId as PlayerId
  const nextAggression = ledgerDefender
    ? {
        ...(state.aggression ?? {}),
        [attackerId]: {
          ...(state.aggression?.[attackerId] ?? {}),
          [ledgerDefender]: (state.aggression?.[attackerId]?.[ledgerDefender] ?? 0) + (result.conquered ? 2 : 1),
        },
      }
    : state.aggression

  // ─── Event log ────────────────────────────────────────────────────────────
  const defenderPlayerId = toTerritory.ownerId as PlayerId
  const now = Date.now()
  const newEvents: GameEvent[] = [
    ...(state.events ?? []),
    {
      kind: 'attack',
      attacker: attackerId,
      defender: defenderPlayerId,
      fromTerritoryId: move.fromTerritoryId,
      toTerritoryId: move.toTerritoryId,
      roll: result.roll,
      attackerLost: result.attackerLost,
      defenderLost: result.defenderLost,
      conquered: result.conquered,
      // Team games mark an attack on a teammate (legal, but the app warns).
      ...(areAllies(state, attackerId, defenderPlayerId) ? { isFriendlyFire: true as const } : {}),
      ts: now,
    } satisfies GameEvent,
  ]

  if (result.conquered) {
    newEvents.push({
      kind: 'conquest',
      attacker: attackerId,
      defender: defenderPlayerId,
      territoryId: move.toTerritoryId,
      armiesMoved: attackerDiceCount,
      ts: now,
    } satisfies GameEvent)

    // Emit one elimination event per eliminated player, in order.
    // bonusGained is 0 when this is a game-ending attack — the bonus armies
    // are never placed because the game ends immediately.
    for (const pid of newlyEliminated) {
      const cardsInherited = (state.cards?.[pid] ?? []).length
      const bonusGained = isGameEndingAttack ? 0 : state.config.eliminatePlayerBonus
      newEvents.push({
        kind: 'elimination',
        eliminator: attackerId,
        eliminated: pid,
        cardsInherited,
        bonusGained,
        ts: now,
      } satisfies GameEvent)
    }
    // Team games: a team whose last member just went out (plan §5).
    newEvents.push(...teamEliminationEvents(state, afterBoard, newlyEliminated, now))

    // Phase-advance event when an elimination triggers the interrupt
    // placement phase. The normal phase-advance pathway lives in
    // turn.ts:advancePhase, but this transition happens directly from
    // applyAttack — so we emit it here for log/spectator parity.
    if (postElimPhase) {
      newEvents.push({
        kind: 'phase-advance',
        from: 'attack',
        to: 'post-elim-placement',
        player: attackerId,
        ts: now,
      } satisfies GameEvent)
    }
  }

  const newState: GameState = {
    ...state,
    map: { ...state.map, territories: newTerritories },
    rngSeed: newSeed,
    attacksThisTurn: state.attacksThisTurn + 1,
    eliminated,
    // lastConquest is preserved when entering post-elim-placement so the
    // free-fortify window remains open. The transfer route accepts both
    // 'attack' and 'post-elim-placement', so the player does the free fortify
    // BEFORE placing their bonus armies (or skips it with armies=0). The
    // post-elim-place exit then clears lastConquest so a skipped window cannot
    // reappear in the resumed attack phase.
    lastConquest,
    conqueredThisTurn,
    ...(nextAggression !== state.aggression ? { aggression: nextAggression } : {}),
    ...(postElimPhase ? { phase: 'post-elim-placement' as const } : {}),
    ...(postElimPhase ? { mustTurnInCards: postElimMustTurnIn } : {}),
    events: newEvents,
    ...(nextCards !== state.cards ? { cards: nextCards } : {}),
    ...(nextBonusArmies !== state.bonusArmies ? { bonusArmies: nextBonusArmies } : {}),
  }

  return {
    state: newState,
    attackerLost: result.attackerLost,
    defenderLost: result.defenderLost,
    conquered: result.conquered,
    roll: result.roll,
    eliminated: newlyEliminated,
  }
}

export interface PostConquestTransferMove {
  /** Number of additional armies to move from source into the conquered
   *  territory. Must satisfy 1 ≤ armies ≤ source.armies - 1. */
  armies: number
}

/**
 * Move additional armies from the attacker's source into the just-conquered
 * territory after a winning attack. Valid only while `state.lastConquest`
 * is set — i.e. immediately after a conquest, before any other action.
 * Does NOT consume the per-turn fortify cap (it's part of the attack phase).
 *
 * Caller is responsible for verifying:
 *   - state.phase === 'attack' OR state.phase === 'post-elim-placement'
 *   - state.lastConquest is non-null and from/to ownership still matches
 *   - source has enough armies to leave ≥1 behind after the move
 *
 * The destination's troop cap IS enforced here. This was the one army inflow
 * that skipped it — reinforce, fortify, post-elim placement and the attack's
 * own occupation all clamp — so a capped territory could be overfilled by
 * conquering into it and then sliding the whole stack forward.
 *
 * Returns a new state with the armies moved and lastConquest cleared.
 * Pure: never mutates the input state.
 */
export function applyPostConquestTransfer(
  state: GameState,
  move: PostConquestTransferMove,
): GameState {
  if (!state.lastConquest) {
    throw new Error('No conquest is pending a transfer')
  }
  const { fromTerritoryId, toTerritoryId } = state.lastConquest
  const dest = state.map.territories.find((t) => t.id === toTerritoryId)
  if (dest) {
    const headroom = armyHeadroom(dest)
    if (move.armies > headroom) {
      throw new Error(
        `'${dest.name}' holds at most ${dest.maxArmies} armies (room for ${headroom} more)`,
      )
    }
  }
  const newTerritories = state.map.territories.map((t) => {
    if (t.id === fromTerritoryId) return { ...t, armies: t.armies - move.armies }
    if (t.id === toTerritoryId) return { ...t, armies: t.armies + move.armies }
    return t
  })

  const player = state.players[state.turnIndex] as PlayerId
  const transferEvent: GameEvent = {
    kind: 'post-conquest-transfer',
    player,
    fromTerritoryId,
    toTerritoryId,
    armies: move.armies,
    ts: Date.now(),
  }

  return {
    ...state,
    map: { ...state.map, territories: newTerritories },
    lastConquest: null,
    events: [...(state.events ?? []), transferEvent],
  }
}

// ─── Blitz attack loop ────────────────────────────────────────────────────────

export interface BlitzAttackMove extends AttackMove {
  /**
   * Optional blitz threshold. When set (> 0), the engine repeats
   * `applyAttack` against the same target until:
   *   - the source territory's armies would risk dropping at or below
   *     `untilTroops` on the next roll, OR
   *   - the target is conquered, OR
   *   - the per-turn attack cap (`canAttack`) is reached, OR
   *   - the source no longer has enough armies to attack (< 2), OR
   *   - the target is already owned by the attacker.
   * When omitted (or 0), exactly one `applyAttack` runs — equivalent to
   * a single-roll attack.
   */
  untilTroops?: number
}

export interface ApplyBlitzAttackResult {
  /** Final game state after every applied roll. */
  state: GameState
  /** Cumulative attacker losses across every applied roll. */
  attackerLost: number
  /** Cumulative defender losses across every applied roll. */
  defenderLost: number
  /** True iff any iteration conquered the target. */
  conquered: boolean
  /** Every roll, in the order applied. Always non-empty when iterations > 0. */
  rolls: DiceRoll[]
  /** Number of `applyAttack` calls executed. Zero means the threshold
   * prevented even a single attack — the caller should surface a 409. */
  iterations: number
}

/**
 * Apply one or more `applyAttack` calls against the same target.
 *
 * When `move.untilTroops` is unset, behaves like a single-roll
 * `applyAttack` wrapped in the same return shape. When set, repeats
 * until any of the stop conditions in `BlitzAttackMove.untilTroops`
 * fires. Dice count is re-clamped each iteration as the source's army
 * count falls.
 *
 * Pure: never mutates `state`; returns a new state along with the
 * cumulative result. The caller does not need to apply the per-turn
 * cap check itself — this function honours `canAttack` between
 * iterations.
 */
export function applyBlitzAttack(
  state: GameState,
  move: BlitzAttackMove,
): ApplyBlitzAttackResult {
  const blitz = move.untilTroops !== undefined && move.untilTroops > 0
  const attackerId = state.players[state.turnIndex] as PlayerId

  let runningState = state
  let attackerLost = 0
  let defenderLost = 0
  let conquered = false
  const rolls: DiceRoll[] = []
  let iterations = 0

  while (true) {
    const src = runningState.map.territories.find((t) => t.id === move.fromTerritoryId)
    const dst = runningState.map.territories.find((t) => t.id === move.toTerritoryId)
    if (!src || !dst) break
    if (src.armies < 2) break
    if (dst.ownerId === attackerId) break // we conquered last iteration
    const dice = Math.min(3, src.armies - 1, move.diceCount)
    if (blitz) {
      // Stop if the worst-case loss on this roll would drop us at or
      // below the threshold (src.armies - dice >= untilTroops keeps us
      // safe).
      if (src.armies - dice < (move.untilTroops as number)) break
    }
    if (!canAttack(runningState).allowed) break
    const result = applyAttack(runningState, {
      fromTerritoryId: move.fromTerritoryId,
      toTerritoryId: move.toTerritoryId,
      diceCount: dice,
    })
    runningState = result.state
    attackerLost += result.attackerLost
    defenderLost += result.defenderLost
    conquered = conquered || result.conquered
    rolls.push(result.roll)
    iterations += 1
    if (!blitz) break
    if (result.conquered) break
  }

  return { state: runningState, attackerLost, defenderLost, conquered, rolls, iterations }
}
